import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { getState, getStateKey, TASK_STATE_META, VISIBLE_STATE_KEYS, STATE_KEY_TO_LEGACY } from '../../../design-system/states';
import { cn } from '../../../lib/utils';

/**
 * OrkaStatusChangeButton — clickable status pill that opens a tight popover
 * listing the 5 user-visible states (To Do · In Progress · Partial · Blocked
 * · Done). Hidden admin states (archived/cancelled/deleted) are NOT in the
 * picker — they are reachable only through admin/batch flows.
 *
 * Works closed OR open: any consumer that wants the legacy single-click
 * behaviour can pass `onClick` and skip `onChange`. If `onChange` is given,
 * the button toggles a popover instead.
 *
 * Props:
 *   state         canonical key or legacy label (resolved via getState)
 *   onChange      (legacyLabel: string) => void — fires with the backend's
 *                 legacy label format ('To Do', 'In Progress', etc.)
 *   onClick       fallback single-action click (used when onChange omitted)
 *   size          'sm' | 'md' (default 'md')
 *   surface       'auto' | 'on-gradient' | 'on-surface'
 *   showChangeHint default true — toggle the chevron
 */
export function OrkaStatusChangeButton({
  state,
  onChange,
  onClick,
  size = 'md',
  surface = 'auto',
  showChangeHint = true,
  className,
  'data-testid': testId = 'orka-status-change-button',
}) {
  const meta = getState(state) || TASK_STATE_META.open;
  const currentKey = getStateKey(state);
  const color = meta.color;
  const height = size === 'sm' ? 30 : 38;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', (e) => e.key === 'Escape' && setOpen(false));
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const bg = surface === 'on-gradient'
    ? 'rgba(255,255,255,0.97)'
    : 'var(--orka-bg-raised, #fff)';
  const border = surface === 'on-gradient'
    ? 'rgba(255,255,255,0.86)'
    : color;

  const handleTrigger = (e) => {
    e.stopPropagation();
    if (onChange) setOpen(o => !o);
    else if (onClick) onClick(e);
  };

  const pick = (key) => {
    setOpen(false);
    if (onChange) onChange(STATE_KEY_TO_LEGACY[key] || key);
  };

  return (
    <span ref={wrapRef} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        onClick={handleTrigger}
        data-testid={testId}
        className={cn(
          'inline-flex items-center gap-2 rounded-[15px] border px-3 font-extrabold shadow-[0_8px_20px_rgba(15,23,42,0.22)] orka-transition-colors hover:-translate-y-px',
          size === 'sm' ? 'text-[12px]' : 'text-[13px]',
        )}
        style={{ height, color, background: bg, borderColor: border }}
        aria-haspopup={onChange ? 'menu' : undefined}
        aria-expanded={onChange ? open : undefined}
        aria-label={`Change status (currently ${meta.label})`}
      >
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          aria-hidden="true"
          style={{ backgroundColor: color, boxShadow: `0 0 0 4px color-mix(in srgb, ${color} 18%, transparent)` }}
        />
        <span className="whitespace-nowrap">{meta.label}</span>
        {showChangeHint && onChange && <ChevronDown className="w-3.5 h-3.5 opacity-70" />}
      </button>
      {open && onChange && (
        <div
          role="menu"
          className="absolute z-50 top-full mt-1 right-0 min-w-[170px] rounded-xl border orka-border-default orka-surface-base shadow-[0_18px_40px_rgba(0,0,0,0.18)] overflow-hidden"
          data-testid="orka-status-change-menu"
        >
          {VISIBLE_STATE_KEYS.map(k => {
            const m = TASK_STATE_META[k];
            const active = currentKey === k;
            return (
              <button
                key={k}
                type="button"
                role="menuitem"
                onClick={(e) => { e.stopPropagation(); pick(k); }}
                className={cn(
                  'w-full text-left px-3 py-2 inline-flex items-center gap-2 text-sm orka-transition-colors',
                  active ? 'orka-surface-subtle font-bold orka-text-primary' : 'orka-text-secondary hover:orka-surface-subtle',
                )}
                data-testid={`orka-status-change-menu-${k}`}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: m.color, boxShadow: active ? `0 0 0 3px color-mix(in srgb, ${m.color} 22%, transparent)` : undefined }}
                />
                <span className="flex-1">{m.label}</span>
                {active && <span className="text-[10px] orka-text-tertiary font-bold">CURRENT</span>}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}

export default OrkaStatusChangeButton;
